import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface ProfilePageProps {
  onNavigateHome?: () => void;
}

export const ProfilePage: React.FC<ProfilePageProps> = ({ onNavigateHome }) => {
  const { user, profile, updateProfile, signOut, loading, profileLoading } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    username: profile?.username || '',
    full_name: profile?.full_name || '',
    email: profile?.email || '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    // Validate username
    if (formData.username.length < 3) {
      setError('Username must be at least 3 characters long');
      setSaving(false);
      return;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      setError('Username can only contain letters, numbers, underscores, and dashes');
      setSaving(false);
      return;
    }

    try {
      const { error } = await updateProfile({
        username: formData.username,
        full_name: formData.full_name,
      });

      if (error) {
        if (error.message.includes('duplicate key value violates unique constraint')) {
          setError('Username is already taken');
        } else {
          setError(error.message);
        }
      } else {
        setSuccess('Profile updated successfully!');
        setEditing(false);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      username: profile?.username || '',
      full_name: profile?.full_name || '',
      email: profile?.email || '',
    });
    setEditing(false);
    setError(null);
    setSuccess(null);
  };

  const handleLogout = async () => {
    await signOut();
    if (onNavigateHome) {
      onNavigateHome();
    }
  };

  const getInitials = () => {
    if (profile?.full_name) {
      return profile.full_name
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (profile?.username) {
      return profile.username.slice(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  if (loading || (user && profileLoading && !profile)) {
    return (
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="flex items-center gap-3 text-gray-400">
            <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
            <span>Loading profile...</span>
          </div>
        </div>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
          <p className="text-gray-400">You must be logged in to view this page.</p>
        </div>
      </main>
    );
  }

  if (!profile) {
    return (
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Profile Not Found</h2>
          <p className="text-gray-400 mb-4">Your profile could not be loaded. This might be a temporary issue.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded-lg"
          >
            Refresh Page
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Profile Settings</h1>
        <p className="text-gray-400">Manage your account settings and preferences.</p>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-900/30 border-2 border-dashed border-red-600 rounded-lg text-red-200">
          <div className="flex items-center gap-2">
            <img 
              src="https://unpkg.com/pixelarticons@1.8.0/svg/close.svg" 
              alt="Error" 
              className="w-5 h-5"
              style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(76%) saturate(2834%) hue-rotate(354deg) brightness(99%) contrast(90%)' }}
            />
            {error}
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-900/30 border-2 border-dashed border-green-600 rounded-lg text-green-200">
          <div className="flex items-center gap-2">
            <img 
              src="https://unpkg.com/pixelarticons@1.8.0/svg/check.svg" 
              alt="Success" 
              className="w-5 h-5"
              style={{ filter: 'brightness(0) saturate(100%) invert(52%) sepia(98%) saturate(464%) hue-rotate(86deg) brightness(118%) contrast(119%)' }}
            />
            {success}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Avatar & Basic Info */}
        <div className="lg:col-span-1">
          <div className="bg-gray-900 border-2 border-gray-800 rounded-lg p-6">
            <div className="text-center">
              {/* Profile Picture */}
              <div className="relative mx-auto mb-4">
                <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-gray-700 flex items-center justify-center text-white font-bold text-lg">
                  {getInitials()}
                </div>
              </div>

              <p className="text-xs text-gray-500 mb-4">Profile picture not available</p>

              <h3 className="text-xl font-bold text-white mb-1">
                {profile.full_name || profile.username}
              </h3>
              <p className="text-gray-400 mb-2">@{profile.username}</p>
              <p className="text-sm text-gray-500 mb-4">{profile.email}</p>
              
              <div className="text-xs text-gray-500 mb-6">
                <p>Member since {new Date(profile.created_at).toLocaleDateString()}</p>
              </div>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 bg-red-600 text-white hover:bg-red-700 transition-colors rounded-lg flex items-center justify-center gap-2"
              >
                <img 
                  src="https://unpkg.com/pixelarticons@1.8.0/svg/logout.svg" 
                  alt="Sign Out" 
                  className="w-4 h-4"
                  style={{ filter: `brightness(0) saturate(100%) invert(1)` }}
                />
                Sign Out
              </button>
            </div>
          </div>
        </div>

        {/* Profile Settings */}
        <div className="lg:col-span-2">
          <div className="bg-gray-900 border-2 border-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Account Information</h2>
              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded-lg flex items-center gap-2"
                >
                  <img 
                    src="https://unpkg.com/pixelarticons@1.8.0/svg/edit.svg" 
                    alt="Edit" 
                    className="w-4 h-4"
                    style={{ filter: 'brightness(0) saturate(100%) invert(1)' }}
                  />
                  Edit Profile
                </button>
              )}
            </div>

            <div className="space-y-6">
              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  disabled={!editing}
                  className={`w-full p-3 bg-gray-800 border-2 rounded-md text-white transition-colors ${
                    editing 
                      ? 'border-gray-700 focus:border-blue-500 focus:outline-none' 
                      : 'border-gray-800 cursor-not-allowed opacity-60'
                  }`}
                  placeholder="Enter your username"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your username is used for login and public display. Must be unique.
                </p>
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  disabled={!editing}
                  className={`w-full p-3 bg-gray-800 border-2 rounded-md text-white transition-colors ${
                    editing 
                      ? 'border-gray-700 focus:border-blue-500 focus:outline-none' 
                      : 'border-gray-800 cursor-not-allowed opacity-60'
                  }`}
                  placeholder="Enter your full name"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your display name shown to other users.
                </p>
              </div>

              {/* Email (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  disabled
                  className="w-full p-3 bg-gray-800 border-2 border-gray-800 rounded-md text-white cursor-not-allowed opacity-60"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Email cannot be changed. Contact support if you need to update it.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            {editing && (
              <div className="flex gap-3 mt-8 pt-6 border-t-2 border-gray-800">
                <button
                  onClick={handleCancel}
                  className="flex-1 p-3 bg-gray-700 text-white hover:bg-gray-600 transition-colors rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 p-3 bg-blue-600 text-white font-medium transition-all duration-200 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 rounded-md"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <img 
                        src="https://unpkg.com/pixelarticons@1.8.0/svg/check.svg" 
                        alt="Save" 
                        className="w-4 h-4"
                        style={{ filter: 'brightness(0) saturate(100%) invert(1)' }}
                      />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Account Stats */}
          <div className="mt-6 bg-gray-900 border-2 border-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">Account Statistics</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-800 border-2 border-gray-700 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-400 mb-1">
                  {Math.floor((Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24))}
                </div>
                <div className="text-sm text-gray-400">Days Active</div>
              </div>
              <div className="bg-gray-800 border-2 border-gray-700 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-400 mb-1">
                  {profile.updated_at !== profile.created_at ? 
                    Math.floor((Date.now() - new Date(profile.updated_at).getTime()) / (1000 * 60 * 60 * 24)) : 
                    0
                  }
                </div>
                <div className="text-sm text-gray-400">Days Since Update</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}; 